import { ApiPost, ApiGet } from './Common.ts'
import { GetOrderEntryConnectorUrl } from '@/stores/SettingsStore.ts'
import { sanitizeBankCode } from '@/validators/BankValidator.ts'

// Interface for bank details response
export interface IPwsBeeBankingResponse {
  items: Array<{
    bankingId: number;
    payoutConfigId: number;
    routingNumber: string;
    accountHolder: string;
    bankName?: string;
    accountNumber: string;
    iban?: string;
    countryCode?: string;
    payoutPercentage: number;
    payPayoutSystemId?: string;
    codeIdAccountType?: {
      codeId: string;
      name: string;
      localizedValue: string;
      categoryName: string;
      categoryId: string;
    };
    codeIdBankingType?: {
      codeId: string;
      name: string;
      localizedValue: string;
      categoryName: string;
      categoryId: string;
    };
    createdDate: string;
    beeNumber: {
      beeNumber: number;
      externalIds: any[];
      salesOrganizationId: string;
    };
    links: {
      rel: string;
      href: string;
      type: string;
    }[];
  }>;
  responseStatus?: {
    errorCode: string;
    message: string;
    stackTrace: string;
    errors: Array<{
      errorCode: string;
      fieldName: string;
      message: string;
      meta: Record<string, string>;
    }>;
    meta: Record<string, string>;
  };
}

export async function BeeBankingCreate(beeNumber: number, bankDetails: {
  bankAccountType: string;
  bankName: string;
  bic: string;
  iban: string;
  directDeposit: boolean;
  opa: boolean;
  payoutPercentage?: number;
  cddCodeIdBankingType: string;
  cddCodeIdAccountType?: string;
}) {
  const url = await GetOrderEntryConnectorUrl('BeeBankingCreateConnector')
  
  const requestData = {
    BeeNumber: beeNumber,
    PayoutConfigId: -1,
    RoutingNumber: sanitizeBankCode(bankDetails.bic),
    AccountHolder: '',
    AccountNumber: sanitizeBankCode(bankDetails.iban),
    CddCodeIdBankingType: bankDetails.cddCodeIdBankingType,
    CddCodeIdAccountType: bankDetails.cddCodeIdAccountType
  }
  
  return ApiPost(url.replace('{BeeNumber}', beeNumber.toString()), requestData)
}

// Update bank account to BOTH type
export async function PwsBeeBankingUpdate(beeNumber: number, bankingId: number, payoutConfigId: number, bankDetails: {
  bankAccountType: string;
  bankName: string;
  bic: string;
  iban: string;
  directDeposit: boolean;
  opa: boolean;
  payoutPercentage?: number;
  cddCodeIdBankingType: string;
  cddCodeIdAccountType?: string;
}) {
  const url = await GetOrderEntryConnectorUrl('PwsBeeBankingUpdate')
  
  const requestData = {
    BeeNumber: beeNumber,
    BankingId: bankingId,
    PayoutConfigId: payoutConfigId,
    RoutingNumber: sanitizeBankCode(bankDetails.bic),
    AccountHolder: bankDetails.bankName,
    AccountNumber: sanitizeBankCode(bankDetails.iban),
    PayoutPercentage: bankDetails.payoutPercentage,
    CddCodeIdBankingType: bankDetails.cddCodeIdBankingType,
    CddCodeIdAccountType: bankDetails.cddCodeIdAccountType
  }
  
  return ApiPost(url.replace('{BeeNumber}', beeNumber.toString()), requestData)
}

// Delete bank account by BankingId
export async function PwsBeeBankingDelete(bankingId: number) {
  const url = await GetOrderEntryConnectorUrl('PwsBeeBankingDelete')
  
  const requestData = {
    BankingId: bankingId
  }
  
  return ApiPost(url.replace('{BankingId}', bankingId.toString()), requestData)
}

// Update payout config percentages
export async function PwsBeePayoutConfigPercentageUpdate(beeNumber: number, percentageUpdates: Array<{
  payoutConfigId: number;
  payoutPercentage: number;
}>) {
  const url = await GetOrderEntryConnectorUrl('PwsBeePayoutConfigPercentageUpdate')
  
  const requestData = {
    BeeNumber: beeNumber,
    PercentageUpdate: percentageUpdates.map(update => ({
      PayoutConfigId: update.payoutConfigId,
      PayoutPercentage: update.payoutPercentage
    }))
  }
  
  return ApiPost(url.replace('{BeeNumber}', beeNumber.toString()), requestData)
}

// Get bank details for a BEE number
export async function PwsBeeBankingGetList(request: {
  beeNumber: number
}): Promise<IPwsBeeBankingResponse> {
  const url = await GetOrderEntryConnectorUrl('BeeBankingGetListConnector')
  return ApiGet(url, request)
}

export async function BankRoutingGetList(filter?: any) {
  const url = await GetOrderEntryConnectorUrl('BankRoutingGetListConnector')
  return ApiGet(url, filter)
}
